import type { FundOpportunity, Offer, SetupValue } from '../types';
import { FIDES_TENANT_ID } from './seedReference';

const PRIVATE_CREDIT = 'Private Credit';

export const FIDES_SETUP_DEFAULTS: Record<string, SetupValue> = {
  'fund.entityType': 'AMIT',
  'fund.trustee': 'Demo Trustee Services Pty Ltd',
  'fund.currency': 'AUD',
  'fund.strategy': PRIVATE_CREDIT,
  'fund.basis': 'FIXED_UNITS',
  'fund.frequency': 'QUARTERLY',
  'tax.aiirType': 'UNIT_TRUST',
  'contact.email': 'registry@fides.example',
  'contact.phone': '03 9000 0000',
  'contact.address': '480 St Kilda Road, Melbourne VIC 3004',
  'contact.website': 'fidescapital.com.au',
  'billing.entity': 'Fides Capital Pty Ltd',
  'privacy.url': 'fidescapital.com.au/privacy',
  'registrar.mode': 'SELF',
  'brand.website': 'fidescapital.com.au',
  'brand.logo': 'FIDES_WORDMARK',
  directors: 'Jason Lont',
  'brand.colour': '#143c34',
  'brand.background': 'NONE',
  'brand.emailHeader': '#143c34',
  'brand.emailButton': '#b08d57',
  'bank.name': 'National Australia Bank',
  'bank.bsb': '083-004',
  'bank.apca': '301500',
  'bank.selfBalancing': true,
  'inv.wholesaleMode': 'PER_INVESTOR_TYPE',
  'inv.verification': 'MANUAL',
  'inv.entityDocs': true,
  'inv.investorBankChange': 'REQUIRE_STATEMENT',
  'inv.staffBankChange': 'REQUIRE_STATEMENT',
  'inv.suspendAccess': false,
  'inv.nameCase': 'AS_ENTERED',
  'class.name': 'Ordinary Units',
  'class.code': 'ORD',
  'class.category': 'ORDINARY',
  'class.issuePrice': '1.00',
  'class.partlyPaid': false,
  'class.fee': '1.5',
  'offer.min': '50000',
  'offer.increment': '1000',
  'class.appRounding': 'DOWN',
  'class.redRounding': 'DOWN',
  'class.unitPrecision': '0',
  'class.pricePrecision': '4',
  'cert.enabled': true,
  'cert.startSeq': '1',
  'cert.title': 'Unit Certificate',
  'cert.terms':
    'Units are held subject to the trust deed. This certificate is not a negotiable instrument; transfers are recorded on the register.',
  'offer.timezone': 'Australia/Melbourne',
  'offer.closeTime': '17:00',
  'offer.countdown': true,
  'offer.issueAtClose': true,
  'offer.notesField': true,
  'offer.useExisting': true,
  'offer.documents': 'Information Memorandum.pdf\nTrust Deed.pdf\nTarget Market Summary.pdf',
  'offer.terms':
    'By applying you confirm you are a wholesale client, have read the Information Memorandum and trust deed, and agree to be bound by the trust deed as a unitholder.',
  'offer.ddReadIm': true,
  'offer.ddWholesale': true,
  'offer.ddRisks': true,
  'offer.collectBank': true,
  'offer.payEft': true,
  'offer.payBpay': true,
  'offer.payDays': '5',
  'offer.payDueMode': 'DAYS',
  'comms.confirmEmail': true,
  'comms.confirmRelease': 'AUTO',
  'comms.reminderRelease': 'APPROVAL',
  'comms.reminderDays': '3',
  'comms.fromName': 'Fides Capital Investor Relations',
  'notify.paymentReceived': true,
  'notify.dailySummary': true,
  'offer.branding': 'INHERIT',
};

export const OPPORTUNITIES: FundOpportunity[] = [
  {
    id: 'op-brg3',
    tenantId: FIDES_TENANT_ID,
    name: 'Fides Bridging Trust 3 (Northgate)',
    code: 'BRG3',
    strategy: PRIVATE_CREDIT,
    basis: 'FIXED_UNITS',
    partlyPaid: false,
    summary:
      'Single first-mortgage bridging loan over a residential subdivision site. 15-month term, quarterly interest distributions.',
    location: 'Northgate (fictional), VIC',
    targetSize: 7_500_000,
    minInvestment: 50_000,
    maxInvestment: 2_000_000,
    increment: 1_000,
    targetReturnPct: 9.5,
    managementFeePct: 1.5,
    distributionFrequency: 'QUARTERLY',
    termMonths: 15,
    eoiCloseDate: '2026-11-20',
    firstCloseDate: '2026-11-27',
  },
  {
    id: 'op-sdf3',
    tenantId: FIDES_TENANT_ID,
    name: 'Fides Senior Debt Fund III',
    code: 'SDF3',
    strategy: PRIVATE_CREDIT,
    basis: 'FIXED_UNITS',
    partlyPaid: false,
    summary:
      'Pooled senior debt fund lending first-mortgage facilities to residential projects. $1.00 units, monthly distributions.',
    location: 'Victoria (pooled)',
    targetSize: 75_000_000,
    minInvestment: 100_000,
    increment: 10_000,
    targetReturnPct: 8.75,
    managementFeePct: 1.25,
    distributionFrequency: 'MONTHLY',
    eoiCloseDate: '2026-12-11',
    firstCloseDate: '2026-12-18',
  },
  {
    id: 'op-opp2',
    tenantId: FIDES_TENANT_ID,
    name: 'Fides Opportunity Fund II',
    code: 'OPP2',
    strategy: 'Opportunistic',
    basis: 'COMMITMENT',
    partlyPaid: true,
    paidOnApplicationPct: 20,
    summary:
      'Closed-ended fund taking preferred equity and JV positions. Units issued partly paid; the balance is called over a three-year investment period.',
    location: 'Victoria (pooled)',
    targetSize: 50_000_000,
    minInvestment: 250_000,
    increment: 50_000,
    targetReturnPct: 15,
    managementFeePct: 2,
    distributionFrequency: 'QUARTERLY',
    termMonths: 60,
    eoiCloseDate: '2027-02-26',
    firstCloseDate: '2027-03-12',
  },
];

export const SEED_OFFERS: Offer[] = [
  {
    id: 'of-sdf2',
    tenantId: FIDES_TENANT_ID,
    fundId: 'f-sdf2',
    classId: 'c-sdf2-a',
    title: 'Fides Senior Debt Fund II offer',
    status: 'PUBLISHED',
    summary:
      'New senior debt fund, first registered natively on the Fides platform. Applications open.',
    url: 'invest.fidescapital.com.au/sdf2/offer',
    closesAt: '2026-12-18T17:00',
    showCountdown: true,
    showInInvestorCentre: true,
    publicApply: false,
    issueAtClose: false,
    minInvestment: 100_000,
    increment: 10_000,
    paymentMethods: ['EFT', 'BPAY'],
    documents: ['Information Memorandum.pdf', 'Trust Deed.pdf'],
    brandColour: '#143c34',
    publishedAt: '2026-09-15T09:00:00',
    publishedBy: 'Jason Lont',
  },
];
